import React, { useState, useEffect, useMemo } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import AssamMap from '../AssamMap';
import CardHead from '../../utils/CardHead';
import { fetchPipelineDistricts, fetchPipelineSkus, fetchFacilityDistribution } from '../../data/api';

export default function TabPipeline() {
  const [districts, setDistricts] = useState([]);
  const [skus, setSkus] = useState([]);
  const [distrib, setDistrib] = useState(null);

  useEffect(() => {
    fetchPipelineDistricts().then(r => setDistricts(r.data)).catch(console.error);
    fetchPipelineSkus().then(r => setSkus(r.data)).catch(console.error);
    fetchFacilityDistribution().then(r => setDistrib(r.data)).catch(console.error);
  }, []);

  const pipelineDonutData = useMemo(() => {
    if (!distrib) return { labels: ['Loading...'], datasets: [{ data: [100], backgroundColor: ['#e5e7eb'] }] };
    const total = parseInt(distrib.total) || 1;
    const above90 = parseInt(distrib.above_90) || 0;
    const mid = (parseInt(distrib.pct_75_90) || 0) + (parseInt(distrib.pct_50_75) || 0);
    const below50 = parseInt(distrib.below_50) || 0;
    const above90Pct = Math.round(above90 * 100 / total);
    const midPct = Math.round(mid * 100 / total);
    const below50Pct = Math.round(below50 * 100 / total);
    return {
      labels: [`Good Stock (${above90Pct}%)`, `Moderate Stock (${midPct}%)`, `Low Stock (${below50Pct}%)`],
      datasets: [{ data: [above90, mid, below50], backgroundColor: ['#34d399', '#fbbf24', '#f87171'], hoverOffset: 8, borderWidth: 2, borderColor: '#fff' }]
    };
  }, [distrib]);

  const top12 = useMemo(() => {
    return [...districts].sort((a, b) => a.avail - b.avail).slice(0, 12);
  }, [districts]);

  const pipelineDistData = useMemo(() => ({
    labels: top12.map(d => d.n),
    datasets: [
      { label: 'Current Availability %', data: top12.map(d => parseFloat(d.avail)), backgroundColor: '#2c5f8a', borderRadius: 3, barThickness: 14 },
    ]
  }), [top12]);

  // Pipeline SKU stats
  const pipelineStats = useMemo(() => {
    if (!skus.length) return { withPipeline: 0, totalPipeline: 0, avgImprovement: 0 };
    const totalPipeline = skus.reduce((s, k) => s + (parseInt(k.pipeline) || 0), 0);
    const avgImprovement = skus.reduce((s, k) => s + ((parseFloat(k.months_with_pipeline) || 0) - (parseFloat(k.months_stock) || 0)), 0) / skus.length;
    return { withPipeline: skus.length, totalPipeline, avgImprovement: avgImprovement.toFixed(1) };
  }, [skus]);

  return (
    <div className={`viz-pane active`}>
      <div className="chart-grid-3" style={{ alignItems: 'stretch' }}>
        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          <CardHead icon="fas fa-map-marked-alt" title="Pipeline Coverage Map" />
          <div className="card-body" style={{ flex: 1, padding: 10 }}><AssamMap mode="avail" /></div>
        </div>
        <div className="card">
          <CardHead icon="fas fa-truck" title="Pipeline Status" />
          <div className="card-body">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { lbl: 'SKUs with Active Pipeline', val: pipelineStats.withPipeline, sub: 'Items with orders in transit', color: '#34d399' },
                { lbl: 'Total Pipeline Quantity', val: pipelineStats.totalPipeline.toLocaleString(), sub: 'Units in pipeline', color: '#fbbf24' },
                { lbl: 'Avg Stock Improvement', val: `+${pipelineStats.avgImprovement} mo`, sub: 'When pipeline arrives', color: '#3b82f6' },
              ].map((c, i) => (
                <div key={i} style={{ background: c.color + '15', border: '1px solid ' + c.color + '30', borderRadius: 8, padding: '12px 16px' }}>
                  <div style={{ fontWeight: 800, fontSize: '1.2rem', color: c.color }}>{c.val}</div>
                  <div style={{ fontWeight: 700, fontSize: '.75rem' }}>{c.lbl}</div>
                  <div style={{ fontSize: '.68rem', color: 'var(--muted)' }}>{c.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          <CardHead icon="fas fa-chart-pie" title="Stock Distribution Breakdown" />
          <div className="card-body" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ maxWidth: 250 }}>
              <Doughnut data={pipelineDonutData} options={{ plugins: { legend: { position: 'bottom', labels: { padding: 14, font: { size: 11 } } }, datalabels: { display: false } }, cutout: '60%', responsive: true, maintainAspectRatio: true }} />
            </div>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 14 }}>
        <div className="card">
          <CardHead icon="fas fa-chart-bar" title="Lowest Availability Districts" subtitle="Bottom 12 districts by stock availability" />
          <div className="card-body" style={{ minHeight: 300 }}>
            {top12.length > 0 ? (
              <Bar data={pipelineDistData} options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { position: 'top' }, datalabels: { display: true, anchor: 'end', align: 'top', color: '#444', font: { weight: '700', size: 10 }, formatter: v => v + '%' } },
                scales: { x: { ticks: { font: { size: 10 } }, grid: { display: false } }, y: { min: 0, max: 100, ticks: { callback: v => v + '%' }, grid: { color: '#f8fafc' } } }
              }} />
            ) : <p style={{color:'#999', textAlign:'center'}}>Loading...</p>}
          </div>
        </div>
      </div>
      {/* Pipeline SKU Table */}
      {skus.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div className="card">
            <CardHead icon="fas fa-table" title="Pipeline SKU Details" subtitle={`Top ${skus.length} SKUs with active pipeline`} />
            <div className="card-body p-0 overflow-auto" style={{ maxHeight: 350 }}>
              <table className="data-table" style={{ fontSize: '.72rem', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Item Name</th>
                    <th>Category</th>
                    <th>Stock</th>
                    <th>AMC</th>
                    <th>Pipeline</th>
                    <th>Months Stock</th>
                    <th>With Pipeline</th>
                  </tr>
                </thead>
                <tbody>
                  {skus.slice(0, 20).map((s, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</td>
                      <td style={{ textAlign: 'center' }}><span style={{ background: s.cat === 'EDL' ? '#dbeafe' : '#fef3c7', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontSize: '.65rem' }}>{s.cat}</span></td>
                      <td style={{ textAlign: 'right' }}>{parseInt(s.stock).toLocaleString()}</td>
                      <td style={{ textAlign: 'right' }}>{parseInt(s.amc).toLocaleString()}</td>
                      <td style={{ textAlign: 'right', color: '#3b82f6', fontWeight: 700 }}>{parseInt(s.pipeline).toLocaleString()}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ background: s.months_stock < 1 ? '#fee2e2' : s.months_stock < 3 ? '#fef3c7' : '#dcfce7', color: s.months_stock < 1 ? '#b91c1c' : s.months_stock < 3 ? '#b45309' : '#15803d', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>{s.months_stock}</span>
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#15803d' }}>{s.months_with_pipeline}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
